import { FC, useMemo } from 'react';

import { ProcessedOutboundTransaction } from 'src/utils/transaction-util';

import PrintWindow from 'src/components/PrintWindow/PrintWindow';

import classes from './GarakInboundCheckForPrint.module.scss';

interface GarakInboundCheckForPrintProps {
  rowData: ProcessedOutboundTransaction[];
}

/** 가락시장 입고확인 대상 구분(groupId) */
const GARAK_GROUP_IDS = ['A', 'B', 'C'];

interface GarakSupplier {
  id: string;
  groupId: string;
  name: string;
}

type RowWithSupplier = ProcessedOutboundTransaction & {
  productSupplier: NonNullable<ProcessedOutboundTransaction['productSupplier']>;
};

/**
 * 가락시장 거래처(구분 A/B/C) 입고확인용 프린트.
 * 출고 데이터에서 해당 구분의 거래처만 중복 제거하여 거래처명 오름차순으로 출력한다.
 */
const GarakInboundCheckForPrint: FC<GarakInboundCheckForPrintProps> = ({ rowData }) => {
  const suppliers = useMemo<GarakSupplier[]>(() => {
    const supplierMap = rowData
      .filter(
        (row): row is RowWithSupplier =>
          row.productSupplier != null && GARAK_GROUP_IDS.includes(row.productSupplier.groupId),
      )
      .reduce((map, row) => {
        const supplier = row.productSupplier;
        if (!map.has(supplier._id)) {
          map.set(supplier._id, { id: supplier._id, groupId: supplier.groupId, name: supplier.name });
        }
        return map;
      }, new Map<string, GarakSupplier>());

    return Array.from(supplierMap.values()).sort((a, b) => a.name.localeCompare(b.name, 'ko'));
  }, [rowData]);

  return (
    <PrintWindow>
      <div className={classes.container}>
        <h1 className={classes.title}>가락시장 입고확인 ({suppliers.length})</h1>
        {suppliers.length === 0 ? (
          <p className={classes.empty}>해당 구분(A/B/C)의 거래처가 없습니다.</p>
        ) : (
          <div className={classes.columns}>
            {suppliers.map((supplier) => (
              <div key={supplier.id} className={classes.item}>
                <span className={classes.groupBadge}>{supplier.groupId}</span>
                <span className={classes.name}>{supplier.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </PrintWindow>
  );
};

export default GarakInboundCheckForPrint;
